/* @HEADER
 * @version 1.0.975 | 2026-09-02
 * @purpose ZVX-DEV-5092 — the left-nav empty state shown when no project of the active organization is visible.
 * @sidecar BoardNavEmptyState.tsx.header.md
 * @layer app | @hex adapter | @ctx kanban
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-5092, ZVX-DEV-5483

export interface BoardNavEmptyStateProps {
  title: string
  hint: string
  createLabel: string
  /** Absent when the host has nowhere to create — then no action is drawn. */
  onCreate?: () => void
  /**
   * ZVX-DEV-5483 — the same empty state now stands in TWO places at once: the
   * left nav and the main region beside it. Both are on screen together, so
   * they cannot share one `data-testid` — a selector would find two nodes and
   * a test could not say which one it read. The left nav keeps the ids it has
   * always had; a second host passes its own.
   */
  testIds?: { root: string; create: string }
}

/** The left nav's ids, kept as the default so no existing selector moves. */
const BOARD_NAV_TESTIDS = { root: 'board-nav-other-org-note', create: 'board-nav-other-org-create' }

/**
 * What the left navigation shows when the project on screen belongs to another
 * organization than the one the switcher names.
 *
 * ## Why this is a component and not three lines inside AppShell
 *
 * AppShell cannot be rendered in a test without standing up most of the app,
 * so the thing that used to live there was proved by nothing at all — and it
 * showed. It carried the class `board-nav__other-org-note`, which existed in NO
 * stylesheet in the repository: it drew as unstyled body text for as long as it
 * shipped, and no check could have noticed, because a class name that matches
 * nothing is not an error anywhere.
 *
 * ## Why the copy talks about visibility
 *
 * The wording it replaces asked the reader to «choose a project of this
 * organization» — in the one situation where this renders, which is when none
 * is visible to choose. It also spoke about what the organization CONTAINS, and
 * the device cannot know that: it knows what is visible to this user. Projects
 * may exist and be closed to them. That distinction is also why creating a
 * project must never happen automatically here — it would mint a duplicate
 * inside an organization that already has one the user cannot see.
 */
export function BoardNavEmptyState({
  title,
  hint,
  createLabel,
  onCreate,
  testIds = BOARD_NAV_TESTIDS,
}: BoardNavEmptyStateProps) {
  return (
    <div className="board-nav__empty" data-testid={testIds.root}>
      <p className="board-nav__empty-title">{title}</p>
      <p className="board-nav__empty-hint">{hint}</p>
      {onCreate && (
        <button
          type="button"
          className="board-nav__empty-action"
          data-testid={testIds.create}
          onClick={onCreate}
        >
          {createLabel}
        </button>
      )}
    </div>
  )
}
